import React, { useEffect } from 'react';
import { useAdminStore } from '../stores/adminStore';

export const TeamOverview: React.FC = () => {
  const {
    teams,
    loading,
    teamsPage,
    teamsTotalPages,
    loadTeams
  } = useAdminStore();

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  const handlePageChange = (page: number) => {
    loadTeams(page);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && teams.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">团队概览</h3>
          <p className="text-sm text-gray-500 mt-1">
            共 {teams.length} 个团队，第 {teamsPage} 页 / 共 {teamsTotalPages} 页
          </p>
        </div>
      </div>

      {/* Teams Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {teams.map((team) => (
          <div key={team.id} className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="text-lg font-medium text-gray-900 mb-2">
                  {team.name}
                </h4>
                {team.description && (
                  <p className="text-sm text-gray-600 mb-4">
                    {team.description}
                  </p>
                )}

                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    创建者: {team.creator.name}
                  </div>
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0h6a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V9a2 2 0 012-2h0" />
                    </svg>
                    邀请码: {team.inviteCode}
                  </div>
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0h6a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V9a2 2 0 012-2h0" />
                    </svg>
                    创建时间: {formatDate(team.createdAt)}
                  </div>
                </div>
              </div>

              {team.creator.avatar && (
                <div className="flex-shrink-0">
                  <img
                    className="h-10 w-10 rounded-full"
                    src={team.creator.avatar}
                    alt={team.creator.name}
                  />
                </div>
              )}
            </div>

            {/* Statistics */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {team.memberCount}
                  </div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">
                    成员数量
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {team.taskCount}
                  </div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">
                    任务数量
                  </div>
                </div>
              </div>
            </div>

            {/* Activity indicator */}
            <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
              <span>团队活跃度</span>
              <div className="flex items-center space-x-1">
                {[...Array(5)].map((_, i) => {
                  const isActive = i < Math.min(Math.ceil((team.memberCount + team.taskCount) / 10), 5);
                  return (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full ${
                        isActive ? 'bg-green-400' : 'bg-gray-200'
                      }`}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {teamsTotalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            显示第 {(teamsPage - 1) * 20 + 1} - {Math.min(teamsPage * 20, teams.length)} 项
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(teamsPage - 1)}
              disabled={teamsPage <= 1 || loading}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              上一页
            </button>
            <span className="px-3 py-2 text-sm font-medium text-gray-700">
              {teamsPage} / {teamsTotalPages}
            </span>
            <button
              onClick={() => handlePageChange(teamsPage + 1)}
              disabled={teamsPage >= teamsTotalPages || loading}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              下一页
            </button>
          </div>
        </div>
      )}

      {teams.length === 0 && !loading && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">暂无团队</h3>
          <p className="mt-1 text-sm text-gray-500">系统中还没有团队。</p>
        </div>
      )}
    </div>
  );
};